import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Users, 
  Calendar,
  ChevronRight,
  Edit2,
  Save,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SeasonDetail = () => {
  const { seasonId } = useParams();
  const navigate = useNavigate();
  const [season, setSeason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [playerCount, setPlayerCount] = useState(0);
  const [matchCounts, setMatchCounts] = useState({});

  useEffect(() => {
    fetchSeason();
    fetchStats();
  }, [seasonId]);

  const fetchSeason = async () => {
    try {
      const response = await axios.get(`${API}/seasons/${seasonId}`);
      setSeason(response.data);
      setEditData({
        club_name: response.data.club_name,
        team_name: response.data.team_name,
        title: response.data.title,
        season_parts: response.data.season_parts
      });
    } catch (error) {
      toast.error("Failed to load season");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const [playersRes, matchesRes] = await Promise.all([
        axios.get(`${API}/players?season_id=${seasonId}`),
        axios.get(`${API}/matches?season_id=${seasonId}`)
      ]);
      setPlayerCount(playersRes.data.length);
      
      // Count matches per season part
      const counts = {};
      matchesRes.data.forEach(m => {
        counts[m.season_part_id] = (counts[m.season_part_id] || 0) + 1;
      });
      setMatchCounts(counts);
    } catch (error) {
      console.error("Failed to fetch stats", error);
    }
  };

  const handleSave = async () => {
    try {
      const response = await axios.put(`${API}/seasons/${seasonId}`, editData);
      setSeason(response.data);
      setEditing(false);
      toast.success("Season updated successfully");
    } catch (error) {
      toast.error("Failed to update season");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  if (!season) return null;

  return (
    <div data-testid="season-detail-page" className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            className="h-10 w-10"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            {editing ? (
              <Input
                value={editData.club_name}
                onChange={(e) => setEditData({ ...editData, club_name: e.target.value })}
                className="text-xs font-medium uppercase tracking-wider text-blue-600 mb-1 h-7 w-48"
                placeholder="Club name"
                data-testid="edit-club-name-input"
              />
            ) : (
              <p className="text-xs font-medium uppercase tracking-wider text-blue-600 mb-1">
                {season.club_name}
              </p>
            )}
            {editing ? (
              <Input
                value={editData.title}
                onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                className="text-2xl font-bold h-auto py-1"
                data-testid="edit-title-input"
              />
            ) : (
              <h1 
                className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                {season.title}
              </h1>
            )}
            {editing ? (
              <div className="flex items-center gap-2 mt-1">
                <span className="text-slate-500">Team:</span>
                <Input
                  value={editData.team_name}
                  onChange={(e) => setEditData({ ...editData, team_name: e.target.value })}
                  className="h-8 w-32"
                  placeholder="Team name"
                  data-testid="edit-team-name-input"
                />
              </div>
            ) : (
              <p className="text-slate-500 mt-1">Team: {season.team_name}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditing(false);
                  setEditData({
                    club_name: season.club_name,
                    team_name: season.team_name,
                    title: season.title,
                    season_parts: season.season_parts
                  });
                }}
                data-testid="cancel-edit-btn"
              >
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="save-edit-btn"
              >
                <Save className="w-4 h-4 mr-1" />
                Save
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(true)}
              data-testid="edit-season-btn"
            >
              <Edit2 className="w-4 h-4 mr-1" />
              Edit
            </Button>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card 
          className="bg-white border border-slate-200 rounded-xl shadow-sm card-hover cursor-pointer group"
          onClick={() => navigate(`/season/${seasonId}/players`)}
          data-testid="manage-players-card"
        >
          <CardContent className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Manage Players</h3>
                <p className="text-sm text-slate-500">{playerCount} players in roster</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-0 rounded-xl shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Season Parts</h3>
                <p className="text-sm text-slate-300">{season.season_parts?.length || 0} parts configured</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Season Parts */}
      <div>
        <h2 
          className="text-xl font-semibold tracking-normal uppercase text-slate-900 mb-4"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          Season Parts
        </h2>
        <div className="space-y-3">
          {season.season_parts?.map((part, index) => (
            <Card 
              key={part.id}
              className="bg-white border border-slate-200 rounded-xl shadow-sm card-hover cursor-pointer group fade-in"
              style={{ animationDelay: `${index * 0.05}s` }}
              onClick={() => navigate(`/season/${seasonId}/matches/${part.id}`)}
              data-testid={`season-part-card-${part.id}`}
            >
              <CardContent className="p-5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center text-lg font-bold text-slate-500" style={{ fontFamily: 'var(--font-display)' }}>
                    {index + 1}
                  </span>
                  <div>
                    <h3 className="font-semibold text-slate-900">{part.name}</h3>
                    <p className="text-sm text-slate-500">
                      {matchCounts[part.id] || 0} matches scheduled
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="bg-slate-50">
                    <Calendar className="w-3 h-3 mr-1" />
                    Matches
                  </Badge>
                  <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};
